export const TEAM_ROUTES = {
  myTeam: '/my-team',
  favoriteTeam: '/favorite-team',
  teamDetail: '/team-detail',
  pickTeam: '/pick-team',
} as const

export const TOURNAMENT_ROUTES = {
  tournamentDetail: '/tournament-detail',
} as const

/**
 * Builds a URL for the event detail page.
 *
 * @example
 * eventDetailRoute('123') // '/event-detail?id=123'
 */
export const eventDetailRoute = (id: string): string => {
  return `/event-detail?id=${id}`
}

/**
 * Builds a URL for the content detail page.
 *
 * @example
 * contentDetailRoute('456') // '/content-detail?id=456'
 */
export const contentDetailRoute = (id: string): string => {
  return `/content-detail?id=${id}`
}

/**
 * Builds a URL for the my-team page.
 *
 * @example
 * myTeamRoute('789') // '/my-team?id=789'
 */
export const myTeamRoute = (id: string): string => {
  return `${TEAM_ROUTES.myTeam}?id=${id}`
}

/**
 * Builds a URL for the pick-team page, with an optional `referrer` for
 * back-navigation once the pick is confirmed.
 *
 * @example
 * pickTeamRoute()                                // '/pick-team'
 * pickTeamRoute({ referrer: '/favorite-team' })   // '/pick-team?referrer=%2Ffavorite-team'
 */
export const pickTeamRoute = (params: { referrer?: string } = {}): string => {
  if (!params.referrer) return TEAM_ROUTES.pickTeam

  const searchParams = new URLSearchParams({ referrer: params.referrer })
  return `${TEAM_ROUTES.pickTeam}?${searchParams.toString()}`
}

/**
 * Builds a URL for a team's page. The same actor serves two routes on purpose:
 * `/favorite-team` is the viewer's own team and sits behind the auth gate its
 * scene declares, while `/team-detail` is any team and is anonymous, because
 * it is reached from the public standings table.
 *
 * @example
 * teamDetailRoute()                            // '/favorite-team'
 * teamDetailRoute('5af1d0c6d5ab721ff036f4fe')  // '/team-detail?id=5af1d0c6d5ab721ff036f4fe'
 */
export const teamDetailRoute = (id?: string): string => {
  if (!id) return TEAM_ROUTES.favoriteTeam

  const searchParams = new URLSearchParams({ id })
  return `${TEAM_ROUTES.teamDetail}?${searchParams.toString()}`
}

/**
 * Builds a URL for the tournament detail page. Built with `URLSearchParams`
 * rather than interpolation so that adding the page's other param — a `tab`
 * whose keys carry a `:` (`stage:apertura`) — stays a safe change here. The
 * tab itself is written by nuqs once the page owns it, never by this builder.
 *
 * @example
 * tournamentDetailRoute('6702c9f1a2b3d4e5f6071801')
 * // '/tournament-detail?id=6702c9f1a2b3d4e5f6071801'
 */
export const tournamentDetailRoute = (id: string): string => {
  const searchParams = new URLSearchParams({ id })
  return `${TOURNAMENT_ROUTES.tournamentDetail}?${searchParams.toString()}`
}
